import type { AuthUser } from '@pixelworld/shared';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchCurrentUser, logout } from './auth-api';
import { AuthContext, type AuthState } from './auth-context';

/** Knows who is logged in. Asks the server once on load; the session itself lives in a cookie. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentUser().then((result) => {
      if (cancelled) return;
      setState(result.ok ? { status: 'ready', user: result.data.user } : { status: 'offline' });
    });
    return () => {
      cancelled = true;
    };
  }, [generation]);

  const signedIn = useCallback((user: AuthUser) => setState({ status: 'ready', user }), []);
  const signOut = useCallback(async () => {
    await logout();
    setState({ status: 'ready', user: null });
  }, []);
  const refresh = useCallback(() => setGeneration((value) => value + 1), []);

  const value = useMemo(
    () => ({ state, signedIn, signOut, refresh }),
    [state, signedIn, signOut, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
